import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {ALL_TIME_END, ALL_TIME_NAME, ALL_TIME_START, POLICY_MODEL, RULE_MODEL} from '../Model/Model';
import {
    CreatePayrollPolicyGroup,
    GetPayrollPolicy
} from "../../../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi";
import routes from "../../../../../../../../Routes/routes";
import {START_TIME} from "../../../../../Employees/Model/Models";
import {errorMsg, validateDecimal} from "../../../../../../../../Apis/functions";
import HandleChange from "../../../../../../../../Helpers/HandleChange";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

const ClonePolicyApp = () => {
    const location = useLocation();
    const state = location.state;
    const id = state?.id ?? null;
    const isGlobal = state?.isGlobal ?? true;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((s) => s?.CommonsSlice?.development);
    const { permission } = useSelector((s) => s.authenticatioauthennSlice);

    const [item, setItem] = useState({ ...RULE_MODEL, isGlobal: isGlobal });
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const [isValidCalculationValue, setIsValidCalculationValue] = useState(true);
    const [isValidCalculationValueText, setIsValidCalculationValueText] = useState('this_value_must_be_greater_than_0');
    const { handleChange: baseHandleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * Re-validates scope and calculation value whenever the item model changes.
     */
    useEffect(() => {
        setIsValid(item.isValidScope());
        const { isValid: calcValid, text: calcText } = validateDecimal(item.calculationValue);
        setIsValidCalculationValue(calcValid);
        if (calcText) setIsValidCalculationValueText(calcText);
    }, [item]);

    /**
     * Loads the source policy on mount using the id from navigation state.
     */
    useEffect(() => {
        if (id) getPolicyById(id);
    }, []);

    /**
     * Handles field changes, applying special logic for allTime and condition fields.
     * @param {React.ChangeEvent} e - The change event from the input.
     */
    const handleChange = (e) => {
        const { name: field, value } = e.target;
        if (field === ALL_TIME_NAME) {
            setItem((prev) => ({
                ...prev,
                [field]: value,
                startTime: ALL_TIME_START,
                endTime: value ? ALL_TIME_END : START_TIME,
            }));
            return;
        }
        if (field === 'condition') {
            setItem(item.changeScope(value));
            return;
        }
        baseHandleChange(e);
    };

    const updateRuleField = (field, value) => handleChange({ target: { name: field, value } });
    /**
     * Fetches the source policy by ID, hydrates the form, and clears its id to treat it as new.
     * @param {string} policyId - UUID of the policy to clone.
     */
    const getPolicyById = (policyId) => {
        dispatch(GetPayrollPolicy(policyId)).then(({ payload: { data: { data, code, success } } }) => {
            if (success === true) {
                setItem({...item.set(data), id: null});
            } else {
                errorMsg(code);
            }
        });
    };

    /**
     * Validates the form and dispatches the clone request. Shows a warning toast if validation fails.
     */
    const handleClone = () => {
        if (!item.isValid(false) || !isValid || !isValidCalculationValue) {
            setEmptyFields(true);
            toast.warning(t('please_fill_all_fields'));
            return;
        }

        // toRegisterCloned reads the scope and the name from its own model, so the
        // values of the policy being cloned have to be carried into it.
        const policy = { ...POLICY_MODEL, isGlobal: item.isGlobal, name: item.name };

        dispatch(CreatePayrollPolicyGroup(policy.toRegisterCloned({ rule: item, departments: [] }))).then(
            ({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    toast.success(t('has_been_cloned_successfully'));
                    goBack();
                } else {
                    errorMsg(code);
                }
            }
        );
    };

    /**
     * Navigates back to the previous screen using the backUrl from navigation state.
     */
    const goBack = () => {
        if (state?.backUrl) {
            navigate(state.backUrl, { state: { ...state } });
        } else {
            navigate(routes.attendance.main);
        }
    };

    return {
        t,
        DEV,
        permission,
        dispatch,
        id,
        isGlobal,
        item,
        setItem,
        emptyFields,
        setEmptyFields,
        isValid,
        isValidCalculationValue,
        isValidCalculationValueText,
        handleClone,
        goBack,
        handleChange,
        updateRuleField
    };
};

export default ClonePolicyApp;
